/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useToast } from '@/components/toast';
import { useNavigate } from '@/hooks/use-navigate';
import { COUPON_ADDED_PARAM } from '@/lib/cart/coupon-query';

/** Show the one-time coupon success toast emitted by the coupon query middleware. */
export function CouponCodeToast(): null {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const firedFor = useRef<string | null>(null);

    useEffect(() => {
        const couponCode = searchParams.get(COUPON_ADDED_PARAM);
        if (!couponCode) {
            return;
        }

        const key = `${location.pathname}?${couponCode}`;
        if (firedFor.current === key) {
            return;
        }
        firedFor.current = key;

        addToast(`Coupon ${couponCode} has been added to cart`, 'success');

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(COUPON_ADDED_PARAM);
        const search = nextParams.toString();
        void navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: true });
    }, [addToast, location.pathname, navigate, searchParams]);

    return null;
}
